import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { APIComponentAttributes, ViewpointDetailEntry } from '@ardoq/api-types';
import { References } from '../components/References';
import { EnhancedScopeData } from '@ardoq/data-model';
import { ContentNotFoundState } from '../components/DoqDisplays';
import {
  ReferenceFilterOptions,
  type ReferenceFilterLabels,
} from '../components/ReferenceFilter';
import { ReferencesToolbar } from '../components/ReferencesToolbar';
import { Box } from '@ardoq/layout';
import { Island } from '@ardoq/page-layout';
import { Header3 } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { type ReferenceColumnLabels } from '../components/referenceColumns';

const HeightControl = styled.div`
  height: 100%;
  border-bottom: ${colors.borderSubtle};
`;

export type ReferencesDrawerLabels = {
  title?: (count: number) => ReactNode;
  notFoundContentType?: string;
  notFoundTitle?: string;
  notFoundMessage?: ReactNode;
  emptyStateTitle?: string;
  emptyStateMessage?: ReactNode;
  emptySearchContentType?: string;
  emptySearchTitle?: string;
  emptySearchMessage?: ReactNode;
  columnLabels?: ReferenceColumnLabels;
  filterLabels?: ReferenceFilterLabels;
  parentChildReferenceName?: string;
};

type ReferencesDrawerProps = {
  component?: APIComponentAttributes;
  scopeData?: EnhancedScopeData;
  detailEntries?: ViewpointDetailEntry[];
  onComponentClick?: (componentId: string) => void;
  trackEvent?: (name: string, metadata?: Record<string, unknown>) => void;
  labels?: ReferencesDrawerLabels;
};

const ReferencesDrawer = ({
  component,
  scopeData,
  detailEntries,
  onComponentClick,
  trackEvent,
  labels,
}: ReferencesDrawerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ReferenceFilterOptions>({
    types: new Set(),
    direction: 'all',
  });

  if (!scopeData || !component) {
    return (
      <ContentNotFoundState
        contentType={labels?.notFoundContentType ?? 'references'}
        title={labels?.notFoundTitle}
        message={labels?.notFoundMessage}
      />
    );
  }

  return (
    <HeightControl>
      <Island
        title={
          <Header3>
            {labels?.title?.(scopeData.references.length) ??
              `References (${scopeData.references.length})`}
          </Header3>
        }
        rightContent={
          <ReferencesToolbar
            searchTerm={searchTerm}
            setSearchTerm={searchPhrase => {
              if (trackEvent) {
                trackEvent(
                  'Component overview: used reference search (drawer)'
                );
              }
              setSearchTerm(searchPhrase);
            }}
            setFilters={filters => {
              if (trackEvent) {
                trackEvent(
                  'Component overview: used reference filter (drawer)'
                );
              }
              setFilters(filters);
            }}
            scopeData={scopeData}
            filters={filters}
            filterLabels={labels?.filterLabels}
          />
        }
        withBorder={false}
        maxWidth="100%"
        minWidth="100%"
        bodySpacing="none"
        showHeaderBorder={false}
      >
        <Box paddingBottom="xlarge">
          <References
            component={component}
            scopeData={scopeData}
            onComponentClick={onComponentClick}
            filters={filters}
            detailEntries={detailEntries}
            searchTerm={searchTerm}
            emptyStateTitle={labels?.emptyStateTitle}
            emptyStateMessage={labels?.emptyStateMessage}
            emptySearchContentType={labels?.emptySearchContentType}
            emptySearchTitle={labels?.emptySearchTitle}
            emptySearchMessage={labels?.emptySearchMessage}
            columnLabels={labels?.columnLabels}
            parentChildReferenceName={labels?.parentChildReferenceName}
          />
        </Box>
      </Island>
    </HeightControl>
  );
};

export { ReferencesDrawer };
